import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { useQueueDeviceCommandMutation } from "../../store/api/commandApi";
import { useAppSelector } from "@hooks/redux";
import { useQueryError } from "@hooks/useQueryError";
import type { Device } from "../../types/api";
import { isVersionAtLeast } from "../../utils/version";
import { formatApn } from "./lteApn";

// Same rule as the client and the API
const APN_PATTERN = /^[A-Za-z0-9._-]{1,100}$/;
const MIN_CLIENT_VERSION = "0.9.0";

interface LteApnDialogProps {
  open: boolean;
  onClose: () => void;
  device: Device;
}

const LteApnDialog: React.FC<LteApnDialogProps> = ({ open, onClose, device }) => {
  const isAdmin = useAppSelector((state) => state.auth.user?.role === "admin");
  const [apn, setApn] = useState("");
  const [sent, setSent] = useState(false);
  const [queueCommand, { isLoading, error, reset }] = useQueueDeviceCommandMutation();
  const errorMessage = useQueryError(error);

  const supported = isVersionAtLeast(device.clientVersion, MIN_CLIENT_VERSION);
  const apnValid = APN_PATTERN.test(apn);

  const handleClose = () => {
    reset();
    setApn("");
    setSent(false);
    onClose();
  };

  const send = async (commandType: "get_apn" | "set_apn" | "reset_apn") => {
    setSent(false);
    try {
      await queueCommand({
        deviceId: device.deviceId,
        command: {
          command_type: commandType,
          parameters: commandType === "set_apn" ? { apn } : {},
        },
      }).unwrap();
      setSent(true);
    } catch (err) {
      console.error(`Failed to queue ${commandType}:`, err);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>LTE APN</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Alert severity="info">
            Current APN: <strong>{formatApn(device.lteApn)}</strong>
            {device.lteApnUpdatedAt &&
              ` (reported ${new Date(device.lteApnUpdatedAt).toLocaleString()})`}
          </Alert>

          {!supported && (
            <Alert severity="warning">
              Needs IoT client {MIN_CLIENT_VERSION} or newer; this device runs{" "}
              {device.clientVersion || "an unknown version"}.
            </Alert>
          )}

          <Button
            variant="outlined"
            onClick={() => send("get_apn")}
            disabled={!supported || isLoading}
            startIcon={<RefreshIcon />}
          >
            Query device
          </Button>

          {isAdmin && (
            <Stack spacing={1}>
              <TextField
                label="New APN"
                value={apn}
                onChange={(e) => setApn(e.target.value.trim())}
                disabled={!supported || isLoading}
                error={apn !== "" && !apnValid}
                helperText={
                  apn !== "" && !apnValid
                    ? "Letters, digits, '.', '-' and '_' only, at most 100 characters"
                    : "No username or password"
                }
                fullWidth
              />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                The device keeps the new APN only if it works, otherwise it switches back on its
                own. This takes up to about 4 minutes. <em>Automatic</em> detects the APN for the
                SIM again (images with lte-apn only).
              </Typography>
            </Stack>
          )}

          {sent && (
            <Alert severity="success">
              Command sent. The result appears in the command list, and the APN above updates when
              the device reports it.
            </Alert>
          )}
          {errorMessage && <Alert severity="error">Failed to send command: {errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Close
        </Button>
        {isAdmin && (
          <Button onClick={() => send("reset_apn")} disabled={!supported || isLoading}>
            Automatic
          </Button>
        )}
        {isAdmin && (
          <Button
            onClick={() => send("set_apn")}
            variant="contained"
            disabled={!supported || !apnValid || apn === device.lteApn || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            Apply APN
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LteApnDialog;
